/* Copyright (c) 2026 Xperi. All Rights Reserved. */

/**
 * Sets a value in a Map, evicting the oldest entry (by insertion order) if the
 * map would exceed maxSize. Provides FIFO-like bounded eviction for Map caches
 * to prevent unbounded memory growth on low-memory devices.
 *
 * @param map     - The Map to insert into.
 * @param key     - The key to set.
 * @param value   - The value to associate with the key.
 * @param maxSize - Maximum number of entries allowed (default: 100).
 */
export function boundedSet<K, V>(
  map: Map<K, V>,
  key: K,
  value: V,
  maxSize: number = 100
): void {
  if (maxSize <= 0) {
    return;
  }
  if (map.size >= maxSize && !map.has(key)) {
    const oldest = map.keys().next();
    if (!oldest.done) {
      map.delete(oldest.value);
    }
  }
  map.set(key, value);
}

/**
 * Appends an item to an Array, evicting the oldest entry (index 0) if the
 * array would exceed maxSize. Provides FIFO-like bounded eviction for Array
 * queues to prevent unbounded memory growth on low-memory devices.
 *
 * @param arr     - The Array to push into.
 * @param item    - The item to append.
 * @param maxSize - Maximum number of items allowed (default: 100).
 */
export function boundedPush<T>(
  arr: Array<T>,
  item: T,
  maxSize: number = 100
): void {
  if (maxSize <= 0) {
    return;
  }
  if (arr.length >= maxSize) {
    arr.shift();
  }
  arr.push(item);
}
